import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { HamburgerMenuButton } from '@/components/Layout/mobile/HamburgerMenuButton';
import { ConfirmWithoutDateModal } from '@/components/domain/shared/ConfirmWithoutDateModal';
import {
  DateTimeSheet,
  type DateTimeSheetValue,
} from '@/components/domain/shared/DateTimeSheet';
import { EmailsReadingCard } from '@/components/domain/shared/EmailsReadingCard';
import { ReviewItemCard } from '@/components/domain/shared/ReviewItemCard';
import {
  ReviewItemDetailsSheet,
  type DetailsSection,
} from '@/components/domain/shared/ReviewItemDetailsSheet';
import { StickyConfirmBar } from '@/components/domain/shared/StickyConfirmBar';
import { SystemBanner } from '@/components/domain/shared/SystemBanner';
import { ToastStatus } from '@/components/domain/shared/ToastStatus';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { ReviewTabSkeleton } from '@/components/states/PageSkeletons';
import { Button } from '@/components/ui/Button';
import { BANNERS } from '@/config/banners';
import { quotaExceededMessage } from '@/constants/billing';
import { useAppShell } from '@/hooks/useAppShell';
import { useEmailPlan } from '@/hooks/useEmailQuota';
import { useMonthlyEmailsReviewed } from '@/hooks/useMonthlyEmailsReviewed';
import { useEmailStatusFeed } from '@/hooks/useEmailStatusFeed';
import { ADD_TO_YARUTA_COPY } from '@/constants/addToYarutaCopy';
import { usePermissions } from '@/hooks';
import { captureEvent } from '@/lib/analytics';
import { isReadingStatus } from '@/lib/emailReadState';
import { STILL_READING_AFTER_MS } from '@/lib/emailStatusRun';
import { resolveTimezone, toIsoWithTz } from '@/lib/tz';
import { emailService, reviewService } from '@/services';
import { triggerProcessing } from '@/services/email.service';
import {
  UNDO_TOAST_DURATION_MS,
  useBottomToastStore,
} from '@/stores/bottomToastStore';
import { toast } from '@/stores/toastStore';
import { useUserStore } from '@/stores/userStore';
import type { SubscriptionTier } from '@/types';
import { ItemFormModal, type ItemFormData } from './ItemFormModal';
import { REVIEW_COPY } from './reviewCopy';
import {
  HEADING_FOCUS,
  confirmItemIds as confirmItemIdsOf,
  focusAfterRemoval,
  listedIds,
  type ReviewItemKind,
  type ReviewListItem,
} from './reviewListModel';
import { useReviewList } from './useReviewList';
import { useConfirmAll } from './useConfirmAll';
import { useReviewItemSources } from './useReviewItemSources';
import { useReviewUndo } from './useReviewUndo';

const EMAIL_DISCARDED_TOAST_ID = 'email-discarded';

/** The Undo toast's words for each card's action (YAR-552). */
const SET_ASIDE_TOAST: Record<ReviewItemKind, string> = {
  new: REVIEW_COPY.toastNotAdded,
  change: REVIEW_COPY.toastNotChanged,
  cancelled: REVIEW_COPY.toastEventKept,
};

/** What the plan-limit banner says: the plan, its cap and the month's count. */
interface PlanLimit {
  tier: SubscriptionTier;
  used: number;
  quota: number;
}

/** The top-level card for an id: itself, or the event a task folds under. */
function topCardId(items: readonly ReviewListItem[], id: string): string {
  const parent = items.find((c) => c.tasks.some((t) => t.id === id));
  return parent?.id ?? id;
}

/** The card or folded task with this id on the list. */
function findListed(
  items: readonly ReviewListItem[],
  id: string,
): ReviewListItem | null {
  for (const card of items) {
    if (card.id === id) return card;
    const task = card.tasks.find((t) => t.id === id);
    if (task) return task;
  }
  return null;
}

/**
 * Review v0 page (Review Flow v2.0, Step 1 — YAR-551), behind
 * `VITE_ENABLE_REVIEW_V0`: every pending item of the household in one list.
 *
 * Header; the emails card (what is still being read or failed, YAR-553);
 * one list with no section headings (D1) — changes and cancellations
 * first, then new items; the sticky Confirm all bar. Nothing pending and nothing reading is the all-caught-up state.
 * First sync — nothing yet, every fed email still reading — opens the
 * emails card and explains what will appear, with no bar.
 */
export function ReviewV0Page() {
  const context = useAppShell();
  const householdId = context?.householdId ?? null;
  const navigate = useNavigate();
  const { canReview, resolved } = usePermissions();
  const allowed = !resolved || canReview;
  // Until AppShell has finished resolving the household, "no items" means
  // "not read yet", never "all caught up".
  const householdResolved = context?.householdResolved ?? false;

  // ── Plan limit (YAR-555 AC10–12) ──────────────────────────────────────
  // Re-read on every outside change the list re-reads on (an emails change,
  // a return to the tab — e.g. from Stripe checkout, AC12), after a confirm
  // or an Undo, and on a QUOTA_EXCEEDED refusal.
  const [planKey, setPlanKey] = useState(0);
  // A QUOTA_EXCEEDED refusal is the server's word: it holds the banner even
  // if the re-read it triggers fails (the count read answers 0 on error),
  // until the next outside re-read (emails change, tab return, confirm,
  // Undo) takes over again.
  const [refusal, setRefusal] = useState<PlanLimit | null>(null);
  const rereadPlan = useCallback(() => {
    setRefusal(null);
    setPlanKey((k) => k + 1);
  }, []);
  const planHousehold = allowed ? householdId : null;
  const plan = useEmailPlan(planHousehold, planKey);
  const used = useMonthlyEmailsReviewed(planHousehold, planKey);
  // Unlimited (null), or either read still loading, never shows it. A batch
  // that started under the cap goes through whole and may end above it
  // (AC17): the banner says the cap ("20 of 20"), never past it (F-13).
  const readLimit: PlanLimit | null =
    plan && plan.quota !== null && used !== null && used >= plan.quota
      ? { tier: plan.tier, used: Math.min(used, plan.quota), quota: plan.quota }
      : null;
  const planLimit = readLimit ?? refusal;
  const atPlanLimit = planLimit !== null;

  const list = useReviewList(allowed ? householdId : null, {
    onOutsideChange: rereadPlan,
  });
  const feed = useEmailStatusFeed(allowed ? householdId : null, {
    idlePoll: true,
  });

  const [retryingIds, setRetryingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // The Still reading wording needs a clock while anything is reading.
  const [now, setNow] = useState(() => Date.now());
  const anyReading = feed.emails.some((e) => isReadingStatus(e.status));
  useEffect(() => {
    setNow(Date.now());
    if (!anyReading) return;
    const interval = setInterval(
      () => setNow(Date.now()),
      STILL_READING_AFTER_MS / 4,
    );
    return () => clearInterval(interval);
  }, [anyReading, feed.emails]);

  const listLoading = !householdResolved || list.loading;

  // Once per visit, for an admin, on the first read that answered.
  const openedRef = useRef(false);
  useEffect(() => {
    if (
      openedRef.current ||
      !resolved ||
      !canReview ||
      !householdId ||
      listLoading ||
      list.error
    ) {
      return;
    }
    openedRef.current = true;
    captureEvent('review_v0_opened', { count: list.count });
  }, [resolved, canReview, householdId, listLoading, list.error, list.count]);

  const { refresh: refreshList, hide, unhide } = list;
  const { refresh: refreshFeed } = feed;

  // ── Card actions (YAR-552) ────────────────────────────────────────────
  // Top-level cards and the nested tasks of an open tasks row, by id.
  const cardRefs = useRef(new Map<string, HTMLDivElement>());
  // Read through a ref so a realtime refresh does not rebuild the callbacks
  // that only read the list (the sheet would re-subscribe its keydown and
  // re-apply the scroll lock on a new onClose). Declared before the focus
  // effect so it is current when that effect runs.
  const listItemsRef = useRef(list.items);
  useEffect(() => {
    listItemsRef.current = list.items;
  });
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  // Set-asides whose RPC has not answered yet. A refresh clears every local
  // hide, and one that read before the RPC committed would bring the card
  // back; these are re-hidden before paint until the RPC answers.
  const settingAsideRef = useRef(new Set<string>());
  // Only the latest action is undoable: an older one answering late must
  // not raise its toast over a newer one's, nor take focus. Bumped on
  // unmount so nothing answering after the page left raises a toast.
  const actionSeqRef = useRef(0);
  useEffect(() => {
    const seq = actionSeqRef;
    return () => {
      seq.current += 1;
    };
  }, []);
  // The card an Undo put back; focused once the refetch after it lands.
  const restoredIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (settingAsideRef.current.size === 0) return;
    const ids = listedIds(list.items);
    for (const id of settingAsideRef.current) {
      if (ids.has(id)) hide(id);
    }
  }, [list.items, hide]);

  // AC5/AC6: focus is requested only once its target is on screen (the
  // next card is already rendered; an Undo asks after its refetch landed),
  // so a target that cannot render is dropped rather than kept to jump
  // later.
  // A nested task whose tasks row is closed has no element: its event card
  // takes focus (E-2).
  useEffect(() => {
    if (!focusId) return;
    const target =
      focusId === HEADING_FOCUS
        ? headingRef.current
        : (cardRefs.current.get(focusId) ??
          cardRefs.current.get(topCardId(listItemsRef.current, focusId)));
    target?.focus();
    setFocusId(null);
  }, [focusId]);

  const { arm } = useReviewUndo({
    onUndone: () => {
      const restored = restoredIdRef.current;
      restoredIdRef.current = null;
      // An undone confirm gives emails back to the month's count.
      rereadPlan();
      void refreshList().then(() => {
        if (restored) setFocusId(restored);
      });
    },
    onUndoFailed: () => void refreshList(),
  });

  const handleRetry = useCallback(
    async (emailId: string) => {
      if (!householdId) return;
      setRetryingIds((prev) => new Set(prev).add(emailId));
      try {
        // Atomic and manager-gated on the server (retry_email RPC).
        await emailService.retryEmail(emailId, householdId);
        triggerProcessing().catch(() => {});
      } catch {
        // A failed retry leaves the email as it was; Try again comes back.
      } finally {
        await refreshFeed();
        setRetryingIds((prev) => {
          const next = new Set(prev);
          next.delete(emailId);
          return next;
        });
      }
    },
    [householdId, refreshFeed],
  );

  const handleDiscard = useCallback(
    async (emailId: string) => {
      // Discard stays enabled on the card while that email's Try again is
      // in flight; the two must not race.
      if (retryingIds.has(emailId)) return;
      try {
        // Immediate, no confirmation and no Undo (YAR-553 AC5). The original
        // stays in Gmail.
        await emailService.dismissEmail(emailId);
        const row = feed.emails.find((e) => e.id === emailId);
        const headline =
          row?.source === 'upload'
            ? ADD_TO_YARUTA_COPY.discardedToast(row.uploadKind ?? 'photo')
            : REVIEW_COPY.emailDiscarded;
        const { showStatus, dismissStatus } = useBottomToastStore.getState();
        showStatus({
          id: EMAIL_DISCARDED_TOAST_ID,
          announcement: headline,
          durationMs: UNDO_TOAST_DURATION_MS,
          render: () => (
            <ToastStatus
              icon="circle-check"
              headline={headline}
              secondLine={[]}
              onClose={() => dismissStatus(EMAIL_DISCARDED_TOAST_ID, 'close')}
            />
          ),
        });
      } catch {
        // The card stays; the refetch below shows the true state.
      } finally {
        void refreshFeed();
        void refreshList();
      }
    },
    [retryingIds, feed.emails, refreshFeed, refreshList],
  );

  const listCount = list.count;
  const handleSetAside = useCallback(
    async (item: ReviewListItem) => {
      const seq = ++actionSeqRef.current;
      // YAR-555 AC7: when this takes the list's last card (a new event takes
      // its folded tasks with it; a change card leaves them), the toast says
      // how many items left (F-12).
      const before = listCount;
      const emptiesList =
        before === 1 + (item.kind === 'new' ? item.tasks.length : 0);
      // F-11: the last listed task of a silent anchor takes the anchor with
      // it (one batch, one Undo), or the email would wait on an item no one
      // can see. Hide, Undo and focus still key on the task.
      // The server's sweep takes every pending task related to the anchor,
      // so the anchor stays while any other listed card relates to it (a
      // task change or cancel never folds); it is a change card again after
      // the refetch (F-8). Accepted race: a sibling set aside moments earlier
      // and still in flight may be swept into this batch by the server, so
      // this Undo can bring it back too.
      const anchorId = item.silentAnchorId;
      const anchorToTake =
        anchorId &&
        !listItemsRef.current.some(
          (c) =>
            c.id !== item.id &&
            (c.silentAnchorId === anchorId ||
              c.row.related_event_id === anchorId),
        )
          ? anchorId
          : null;
      // Read before the hide: a card goes to its neighbour, a nested task to
      // its sibling or its event (E-2).
      const next = focusAfterRemoval(listItemsRef.current, item.id);
      settingAsideRef.current.add(item.id);
      hide(item.id);
      setFocusId(next);
      try {
        // Soft, recorded as a review batch; the email is finalized by the
        // server when this was its last item (the emails card never lists
        // PROCESSED emails, so nothing else to do here).
        // Only a new event's Don't add takes its tasks (E-5): a change or
        // cancel card's new tasks stay on the list.
        const { batchId } = await reviewService.setAsideReviewItem(
          anchorToTake ?? item.id,
          {
            includeRelatedTasks:
              anchorToTake !== null ||
              (item.type === 'event' && item.kind === 'new'),
          },
        );
        settingAsideRef.current.delete(item.id);
        // A read issued before the RPC answered may still land and clear the
        // hide; a newer read supersedes it (latest wins) and is the truth.
        // With no batch it was no longer pending: nothing was written.
        void refreshList();
        if (!batchId) return;
        captureEvent('review_item_set_aside', { kind: item.kind });
        if (seq !== actionSeqRef.current) return;
        arm({
          batchId,
          label: emptiesList
            ? REVIEW_COPY.toastRemoved(before)
            : SET_ASIDE_TOAST[item.kind],
          restore: () => {
            unhide(item.id);
            restoredIdRef.current = item.id;
            captureEvent('review_item_set_aside_undone', { kind: item.kind });
          },
        });
      } catch {
        settingAsideRef.current.delete(item.id);
        unhide(item.id);
        // Only the latest action may move focus back.
        if (seq === actionSeqRef.current) setFocusId(item.id);
        toast.error(REVIEW_COPY.failHeadline);
      }
    },
    [hide, unhide, refreshList, arm, listCount],
  );

  // ── Confirm all (YAR-555) ─────────────────────────────────────────────
  // A9: exactly the cards on the list (folded tasks included; set-aside
  // cards are already hidden), plus each listed task's silent anchor (F-10).
  // The hook fixes them when the series starts.
  const confirmItemIds = confirmItemIdsOf(list.items);
  const confirm = useConfirmAll({
    householdId: householdId ?? '',
    itemIds: confirmItemIds,
    onConfirmed: (result) => {
      void refreshList();
      // The count moved; the cap may have been reached.
      rereadPlan();
      // Nothing was pending: nothing written, nothing to undo.
      if (!result.batch_id) return;
      captureEvent('review_confirm_all', {
        items: result.items_confirmed,
        emails: result.emails_confirmed,
      });
      // The latest write: an older card action answering late must not
      // raise its toast over this one.
      actionSeqRef.current += 1;
      arm({
        batchId: result.batch_id,
        label: REVIEW_COPY.toastAdded,
        // onUndone refetches: every card comes back. No card to focus.
        restore: () => {
          restoredIdRef.current = null;
        },
      });
    },
    // Nothing was written; show the current list, blame no one.
    onStale: () => void refreshList(),
    // At the cap at batch start: nothing was written. The re-read raises
    // the banner (and disables the bar); the cards stay.
    onQuota: (err) => {
      rereadPlan();
      setRefusal({ tier: err.tier, used: err.used, quota: err.quota });
      void refreshList();
    },
  });
  const { confirm: runConfirmAll, busy: confirmBusy } = confirm;
  const confirmAll = useCallback(() => {
    if (!householdId || atPlanLimit) return;
    void runConfirmAll();
  }, [householdId, atPlanLimit, runConfirmAll]);

  // ── Add a missing date (YAR-554) ──────────────────────────────────────
  // The profile timezone is the zone the cards are read in; the sheet
  // writes in the same one (never the bare default unresolved, F-17).
  const timezone = resolveTimezone(useUserStore((s) => s.timezone));
  const [datingItem, setDatingItem] = useState<ReviewListItem | null>(null);
  const [savingDate, setSavingDate] = useState(false);
  const [askNoDate, setAskNoDate] = useState(false);

  const closeDateSheet = useCallback(() => {
    if (savingDate) return;
    const id = datingItem?.id ?? null;
    setDatingItem(null);
    if (id) setFocusId(id);
  }, [savingDate, datingItem]);

  const handleDateDone = useCallback(
    async (value: DateTimeSheetValue) => {
      const item = datingItem;
      if (!item || !householdId) return;
      setSavingDate(true);
      try {
        await emailService.updateReviewItem(item.id, householdId, {
          start_datetime: value.start,
          end_datetime: value.end,
          // A task's date is its due date (as in today's form).
          ...(item.type === 'task' ? { due_datetime: value.start } : {}),
          is_time_unknown: value.allDay,
        });
        setDatingItem(null);
        setFocusId(item.id);
        void refreshList();
      } catch {
        // The sheet stays with the parent's pick; Done can be tapped again.
        toast.error(REVIEW_COPY.failHeadline);
      } finally {
        setSavingDate(false);
      }
    },
    [datingItem, householdId, refreshList],
  );

  // The item left the list while its sheet was open (confirmed or set
  // aside elsewhere): it is no longer pending, so there is nothing to date.
  useEffect(() => {
    if (!datingItem || savingDate || list.loading || list.error) return;
    if (!listedIds(list.items).has(datingItem.id)) setDatingItem(null);
  }, [datingItem, savingDate, list.loading, list.error, list.items]);

  // ── Details sheet + Edit Details (YAR-551 D2) ──────────────────────────
  const [details, setDetails] = useState<{
    id: string;
    section: DetailsSection;
  } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const detailsItem = details ? findListed(list.items, details.id) : null;
  const sources = useReviewItemSources(detailsItem?.sourceEmailIds ?? null);

  const openDetails = useCallback(
    (item: ReviewListItem, section: DetailsSection) => {
      setDetails({ id: item.id, section });
      captureEvent('review_item_details_opened', { kind: item.kind, section });
    },
    [],
  );
  const openCard = useCallback(
    (item: ReviewListItem) => openDetails(item, 'top'),
    [openDetails],
  );
  const openSources = useCallback(
    (item: ReviewListItem) => openDetails(item, 'sources'),
    [openDetails],
  );

  const closeDetails = useCallback(() => {
    // Escape reaches the sheet under Edit Details too: the editor leaves first.
    if (editingId) return;
    const id = details?.id ?? null;
    setDetails(null);
    // A nested task takes focus itself; the focus effect falls back to its
    // event card when its tasks row is closed (E-13).
    if (id) setFocusId(id);
  }, [editingId, details]);

  // The item left the list while its sheet was open (confirmed or set aside
  // elsewhere): nothing pending is left to show or edit. Focus goes to the
  // list's first card (the heading when it is empty), never to <body>.
  useEffect(() => {
    if (!details || list.loading || list.error) return;
    if (!listedIds(list.items).has(details.id)) {
      setDetails(null);
      setEditingId(null);
      setFocusId(list.items[0]?.id ?? HEADING_FOCUS);
    }
  }, [details, list.loading, list.error, list.items]);

  const editingRow = editingId ? (list.rows.get(editingId) ?? null) : null;
  const handleEditSubmit = useCallback(
    async (data: ItemFormData) => {
      if (!editingRow || !householdId) return;
      // A Confirm all in flight would race the edit (as the card actions).
      if (confirmBusy) return;
      // The form emits naive datetimes; store them offset-aware in the
      // profile timezone, as the per-email screen does (YAR-271).
      const withTz = (v: string | null) => (v ? toIsoWithTz(v, timezone) : v);
      // review_items has no member column (R5): `member_user_ids` is left
      // out so PostgREST does not reject the write. `satisfies` makes a new
      // ItemFormData field a type error here instead of a silent drop.
      // (`recurrence` and `link` are not form fields: the per-email screen
      // never sends them from the editor either.)
      const reviewData = {
        type: data.type,
        title: data.title,
        child_names: data.child_names,
        description: data.description,
        start_datetime: withTz(data.start_datetime),
        end_datetime: withTz(data.end_datetime),
        due_datetime: withTz(data.due_datetime),
        location: data.location,
        links: data.links,
        related_event_id: data.related_event_id,
        is_time_unknown: data.is_time_unknown,
      } satisfies Record<keyof Omit<ItemFormData, 'member_user_ids'>, unknown>;
      try {
        await emailService.updateReviewItem(
          editingRow.id,
          householdId,
          reviewData,
          // A type change drops any update/cancel proposal (same write).
          editingRow.type,
        );
      } catch {
        // The form stays with the parent's edits; Save can be tapped again.
        toast.error(REVIEW_COPY.failHeadline);
        return;
      }
      setEditingId(null);
      void refreshList();
    },
    [editingRow, householdId, confirmBusy, timezone, refreshList],
  );

  // The question only stands while the gate asks it; a refetch that dated
  // the last task (or added an undated event) retires it for good.
  const gateAsks = list.gate.kind === 'needs-task-confirmation';
  useEffect(() => {
    if (!gateAsks) setAskNoDate(false);
  }, [gateAsks]);

  // Undated events block (the bar disables itself); undated tasks ask.
  const handleConfirm = useCallback(() => {
    if (list.gate.kind === 'needs-task-confirmation') {
      setAskNoDate(true);
      return;
    }
    if (list.gate.kind === 'ready') confirmAll();
  }, [list.gate, confirmAll]);

  // Same defense-in-depth as the Inbox: nav hides Review for non-admins, but
  // /review can still be typed in. Bounce them once the role is resolved.
  if (!allowed) {
    return (
      <Navigate
        to="/today"
        replace
      />
    );
  }

  const header = (
    <div>
      {context?.onOpenDrawer && (
        <div className="mb-4">
          <HamburgerMenuButton onClick={context.onOpenDrawer} />
        </div>
      )}
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="text-[32px] font-bold leading-10 text-foreground outline-none"
      >
        {REVIEW_COPY.title}
      </h1>
      <p className="text-base font-medium text-muted-foreground mt-1">
        {REVIEW_COPY.subtitle}
      </p>
    </div>
  );

  const listReady = !listLoading && !list.error;
  const readingCount = feed.emails.filter((e) =>
    isReadingStatus(e.status),
  ).length;
  // Controller ruling: nothing on the list, something being read, and no
  // fed email has produced items yet (failed rows don't count either way).
  const firstSync =
    listReady &&
    list.count === 0 &&
    readingCount > 0 &&
    !feed.emails.some((e) => e.itemCount > 0);
  const showBar = listReady && list.count > 0;
  const showEmpty = listReady && list.count === 0 && readingCount === 0;

  return (
    <div className="flex flex-col min-h-full bg-surface">
      {/* Above the hamburger and the title, full-bleed: it pushes the page
          down (AC10 and AC17 frames, F-14). */}
      {planLimit && (
        <SystemBanner
          banner={{
            ...BANNERS.plan_limit_reached,
            copy: {
              ...BANNERS.plan_limit_reached.copy,
              description: quotaExceededMessage(
                planLimit.tier,
                planLimit.used,
                planLimit.quota,
              ),
            },
          }}
          onAction={() => {
            const route = BANNERS.plan_limit_reached.action?.route;
            if (route) navigate(route);
          }}
        />
      )}
      <div
        className={[
          'px-5 pt-5 flex flex-1 flex-col gap-5',
          showBar ? 'pb-5' : 'pb-24',
        ].join(' ')}
      >
        {header}

        {listLoading ? (
          <ReviewTabSkeleton />
        ) : (
          <>
            {/* Stays on a list error too: failed and reading emails must
                remain actionable. */}
            <EmailsReadingCard
              emails={feed.emails}
              now={now}
              defaultOpen={firstSync}
              retryingIds={retryingIds}
              onRetry={(id) => void handleRetry(id)}
              onDiscard={(id) => void handleDiscard(id)}
            />
            {firstSync && (
              <p className="text-sm text-muted-foreground">
                {REVIEW_COPY.firstSyncHint}
              </p>
            )}
          </>
        )}

        {!listLoading && list.error && (
          <ErrorState onRetry={() => void refreshList()} />
        )}

        {listReady && list.items.length > 0 && (
          <div className="flex flex-col gap-3">
            {list.items.map((item) => (
              <ReviewItemCard
                key={item.id}
                ref={(el: HTMLDivElement | null) => {
                  if (el) cardRefs.current.set(item.id, el);
                  else cardRefs.current.delete(item.id);
                }}
                item={item}
                // A set-aside or a date tapped mid-confirm would be lost
                // under the batch: wait for it to answer.
                actionsDisabled={confirm.busy}
                onAction={(card) => {
                  if (!confirm.busy) void handleSetAside(card);
                }}
                onOpen={openCard}
                onOpenSources={openSources}
                onAddDate={(card) => {
                  if (!confirm.busy) setDatingItem(card);
                }}
                childrenData={list.children}
                timezone={timezone}
                registerTask={(id, el) => {
                  if (el) cardRefs.current.set(id, el);
                  else cardRefs.current.delete(id);
                }}
              />
            ))}
          </div>
        )}

        {showEmpty && (
          <EmptyState
            title={REVIEW_COPY.emptyTitle}
            action={
              <Button
                variant="secondary"
                onClick={() => navigate('/schedule')}
              >
                {REVIEW_COPY.emptyAction}
              </Button>
            }
          />
        )}
      </div>

      {showBar && (
        <StickyConfirmBar
          count={list.count}
          gate={list.gate}
          busy={confirm.busy}
          disabled={atPlanLimit}
          onConfirm={handleConfirm}
        />
      )}

      <DateTimeSheet
        // Remounts per item, so each opening starts from that item.
        // Distinct from the details sheet's fallback: siblings need unique keys.
        key={datingItem?.id ?? 'date-sheet-closed'}
        open={!!datingItem}
        title={datingItem?.title ?? ''}
        // Only undated items carry the tag, so there is nothing to prefill.
        initial={{ start: null, end: null, allDay: false }}
        timezone={timezone}
        busy={savingDate}
        onDone={(value) => void handleDateDone(value)}
        onClose={closeDateSheet}
      />

      <ConfirmWithoutDateModal
        open={askNoDate && list.gate.kind === 'needs-task-confirmation'}
        tasks={
          list.gate.kind === 'needs-task-confirmation' ? list.gate.tasks : []
        }
        onAddDates={() => setAskNoDate(false)}
        onConfirmWithoutDate={() => {
          setAskNoDate(false);
          confirmAll();
        }}
      />

      <ReviewItemDetailsSheet
        // Remounts per item, so each opening starts clean.
        key={details?.id ?? 'details-sheet-closed'}
        item={detailsItem}
        initialSection={details?.section ?? 'top'}
        sources={{
          emails: sources.emails,
          summary: sources.summary,
          loading: sources.loading,
          error: !!sources.error,
          reload: sources.reload,
        }}
        onClose={closeDetails}
        onEdit={(item) => {
          if (!confirm.busy) setEditingId(item.id);
        }}
        editDisabled={confirm.busy}
      />

      {/* After the sheet: same z-index, so it stacks on top. */}
      {editingRow && (
        <ItemFormModal
          mode="edit"
          allowTypeChange
          initialData={editingRow}
          availableEvents={list.items
            .filter(
              (c) => c.type === 'event' && c.emailId === editingRow.email_id,
            )
            .map(({ id, title }) => ({ id, title }))}
          availableChildren={list.children.map((c) => ({
            name: c.name,
            emoji_avatar: c.emoji_avatar,
            emoji_color: c.emoji_color,
            avatar_image_path: c.avatar_image_path,
          }))}
          onSubmit={handleEditSubmit}
          onBack={() => setEditingId(null)}
        />
      )}
    </div>
  );
}
